import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, inject, model, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { BadgeModule } from 'primeng/badge';
import { CheckboxComponent, CheckboxChangeEvent } from '../../../@core/components/ui/checkbox';
import { ChipModule } from 'primeng/chip';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { DragDropModule } from 'primeng/dragdrop';
import { FieldsetModule } from 'primeng/fieldset';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { Textarea } from 'primeng/textarea';
import { ToggleComponent } from '../../../@core/components/ui/toggle/toggle.component';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { SelectComponent, SelectOption, SelectConfig } from '../../../@core/components/ui/select';
import { ANSWER_GENERATION_TYPE, USER_ROLE, USER_TYPE } from '../../../@core/models/enums';
import { CONTRACT_USER_ROLE, DEACTIVATION_STATUS, IAttachment, IProject, IShareContract, IShareUser, IUser, SHARE_ACCESS, SHARE_FEATURE } from '../../../@core/models/model';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { CLIENT_EVENTS, SOCKET_EVENTS, SocketService } from '../../../@core/services/socket.service';
import { ToastService } from '../../../@core/services/toast.service';
import { ChatBubbleComponent } from '../../../@shared/components/chat-bubble/chat-bubble.component';
import { ChatSidebarService } from 'src/app/features/chat/services/chat-sidebar.service';
import { DocumentFormTableComponent } from '../../../@shared/components/document-form/document-form-table/document-form-table.component';
import { DocumentPreviewComponent } from '../../../@shared/components/document-preview/document-preview.component';
import { FileDropComponent } from '../../../@shared/components/file-drop/file-drop.component';
import { FileDropTypes } from '../../../@shared/components/file-drop/file-drop.types';
import { InnformQuestionSidepanelComponent } from '../../../@shared/components/innform-question-sidepanel/innform-question-sidepanel.component';
import { SearchBarComponent } from '../../../@shared/components/search-bar/search-bar.component';
import { ShareAccessDialogComponent } from "../../../@shared/components/share-to-users/components/share-access-dialog/share-access-dialog.component";
import { UserNameDisplayComponent } from '../../../@shared/components/user-name-display/user-name-display.component';
import { ScrollResetDirective } from '../../../@shared/directives/scroll-reset.directive';
import { ThrottleClickDirective } from '../../../@shared/directives/throttle-click.directive';
import { FullNamePipe } from '../../../@shared/pipes/fullName.pipe';
import { UserAvatarPipe } from '../../../@shared/pipes/user-avatar-pipe';
import { downloadDocument } from '../../../common/utils';
import { CreateFormComponent } from '../../../form-management/create-form/create-form.component';
import { SVG_ICONS } from '../../constants/constant';
import { ANSWER_GENERATION_STATUS, DELETE_TYPE, DOCUMENT_FORM_STATUS, FORM_QUESTION_STATUS, IContract, IDocumentForm, IFormAnswer, IFormQuestion, QUESTION_TYPE } from '../../models/contract-management.model';
import { IConstructionAttachment } from '../../models/settings.model';
import { ContractService } from '../../services/contracts.service';
import { DocumentFormService, IFormMetrics } from '../../services/document-form.service';
import { IQnaPair, QnAPairService } from '../../services/qna-pair.service';
import { SharedSourcesService } from '../../services/question-sources.service';
import { ShareContractService } from '../../services/share-contract.service';
import { ShareService } from '../../services/share.service';
import { UserService } from '../../services/user.service';
import { MultiSelectComponent } from '../../../@core/components/ui/multi-select';
import { PopoverCompatibility, PopoverComponent } from 'src/app/@core/components/ui/popover/popover.component';

export type QuestionAnswer = string | string[] | Date | number | string[][] | File[] | File | IConstructionAttachment[];
export type IDocumentFormSection = IDocumentForm['sections'][number];
export type IDocumentFormQA = IDocumentFormSection['form'][number];
export interface IFormFilter {
        assignedToMe?: boolean,
        watchedByMe?: boolean,
        questionStatuses?: FORM_QUESTION_STATUS[],
        answerStatuses?: ANSWER_GENERATION_STATUS[],
        authors?: ANSWER_GENERATION_TYPE[],
        assignees?: string[]
}

import { AssetUrlPipe } from '../../../@shared/pipes/asset-url.pipe';
import { CreditsOverviewComponent } from '../../../features/credits/components/credits-overview/credits-overview.component';
import { UserCreditsStateService } from '../../../features/credits/services/user-credits-state.service';
import { UserCreditsService } from '../../../features/credits/services/user-credits.service';

@Component({
        selector: 'app-document-form',
        imports: [
                AutoCompleteModule,
                SelectModule,
                AccordionModule,
                BadgeModule,
                ProgressSpinnerModule,
                ButtonComponent,
                FormsModule,
                CommonModule,
                ChipModule,
                TooltipModule,
                InputTextModule,
                Textarea,
                CheckboxComponent,
                RadioButtonModule,
                DatePickerModule,
                InputNumberModule,
                ScrollResetDirective,
                ThrottleClickDirective,
                SkeletonModule,
                DialogComponent,
                CreateFormComponent,
                FullNamePipe,
                UserAvatarPipe,
                AvatarModule,
                AvatarGroupModule,
                DragDropModule,
                IconFieldModule,
                FieldsetModule,
                DocumentFormTableComponent,
                ToggleComponent,
                ChatBubbleComponent,
                DocumentPreviewComponent,
                InputGroupAddonModule,
                InputGroupModule,
                FileDropComponent,
                InnformQuestionSidepanelComponent,
                SearchBarComponent,
                TitleCasePipe,
                ShareAccessDialogComponent,
                SelectComponent,
                MultiSelectComponent,
                PopoverComponent,
                AssetUrlPipe,
                UserNameDisplayComponent,
                CreditsOverviewComponent,
        ],
        standalone: true,
        providers: [DocumentFormService, ToastService],
        templateUrl: './document-form.component.html',
        styleUrl: './document-form.component.scss',
        animations: [
                trigger('genieEffect', [
                        state('void', style({
                                transform: 'scale(0.3) translateY(20px)',
                                opacity: 0
                        })),
                        state('visible', style({
                                transform: 'scale(1) translateY(0)',
                                opacity: 1
                        })),
                        transition('void => visible', [
                                animate('300ms cubic-bezier(0.34, 1.56, 0.64, 1)')
                        ]),
                        transition('visible => void', [
                                animate('200ms ease-out')
                        ])
                ])
        ]
})

export class DocumentFormComponent implements OnInit, OnDestroy, OnChanges {
        @ViewChild('editableContent', { static: false }) editableContent!: ElementRef;
        @ViewChild('watcherOverlay', { static: false }) watcherOverlay!: PopoverComponent;
        @ViewChild('filterMainOverlay') filterMainOverlay!: PopoverComponent;
        @ViewChild('statusFilter') statusFilterOverlay!: PopoverComponent;
        @ViewChild('authorFilter') authorFilterOverlay!: PopoverComponent;
        @ViewChild('assigneeFilter') assigneeFilterOverlay!: PopoverComponent;

        @Input() contract!: IContract;
        @Input() currentTabIndex: number = 0;

        @Input() projectId: string = '';

        documentForm!: IDocumentForm;
        loading: boolean = true;
        isApplyingFilters: boolean = false;

        //enums
        QUESTION_TYPE = QUESTION_TYPE;
        FORM_QUESTION_STATUS = FORM_QUESTION_STATUS;
        DOCUMENT_FORM_STATUS = DOCUMENT_FORM_STATUS;
        ANSWER_GENERATION_STATUS = ANSWER_GENERATION_STATUS;
        DELETE_TYPE = DELETE_TYPE;
        SVG_ICONS = SVG_ICONS;
        DEACTIVATION_STATUS = DEACTIVATION_STATUS;

        SHARE_FEATURE = SHARE_FEATURE;



        permissions: {
                canEdit: boolean,
                canComment: boolean,
                canView: boolean
        } = {
                        canEdit: false,
                        canComment: false,
                        canView: false
                }
        isOwner: boolean = false;
        isEditor: boolean = false;
        currentRole: string = '';
        currentUser!: IUser;
        isSameOrganization: boolean = false;
        shareDetails?: IShareContract;

        // Unified approach for all question types
        questionAnswers: { [questionId: string]: QuestionAnswer } = {};
        originalAnswers: { [questionId: string]: QuestionAnswer } = {};
        metrics: IFormMetrics = {
                totalExtractedQuestions: 0,
                totalAnsweredQuestions: 0,
                totalPendingQuestions: 0,
                totalApprovedQuestions: 0,
                totalRejectedQuestions: 0,
                totalFlaggedQuestions: 0,
        };
        formattedOptions: { [key: string]: { label: string, value: string }[] } = {};

        // Socket subscriptions
        documentFormQuestionExtractedSubscription!: Subscription;
        documentFormAnswerExtractedSubscription!: Subscription;
        documentFormStatusSubscription!: Subscription;
        answerGenerationStatusSubscription!: Subscription;

        showEditForm: boolean = false;
        showAddQuestionDialog: boolean = false;
        questionData: Partial<IFormQuestion> = {};
        questionTypeOptions: SelectOption[] = [
                { label: 'Text', value: QUESTION_TYPE.TEXT },
                { label: 'Single Select', value: QUESTION_TYPE.SINGLE_SELECT },
                { label: 'Multiple Select', value: QUESTION_TYPE.MULTIPLE_SELECT },
                { label: 'Radio', value: QUESTION_TYPE.RADIO },
                { label: 'Checkbox', value: QUESTION_TYPE.CHECKBOX },
                { label: 'Table', value: QUESTION_TYPE.TABLE },
                { label: 'Email', value: QUESTION_TYPE.EMAIL },
                { label: 'Other', value: QUESTION_TYPE.OTHER },
        ];
        selectedSection: string = '';

        // SelectConfig for question type in add dialog
        questionTypeConfig: SelectConfig = {
                placeholder: 'Select Type',
                searchable: false,
                clearable: false,
                appendTo: 'body'
        };

        // SelectConfig for single select questions (dynamic based on permissions)
        getSingleSelectConfig(disabled: boolean): SelectConfig {
                return {
                        placeholder: 'Select an option',
                        searchable: false,
                        clearable: true,
                        showClear: true,
                        appendTo: 'body',
                        disabled: disabled,
                        styleClass: 'w-full'
                };
        }

        assignUserToQuestionDialog: boolean = false;

        users: IUser[] = [];
        questionApprovers: IShareUser[] = [];
        autoCompleteUsers: IUser[] = [];
        tempQuestionApprovers: any[] = [];
        originalQuestionApprovers: any[] = [];
        tempQuestionApproversSet: Set<string> = new Set();
        autoCompleteValue: string = '';
        selectedQuestion: IFormQuestion | null = null;
        editApprover: { [key: number]: boolean } = {};
        draggedApproverIdx: number | null = null;
        USER_TYPE = USER_TYPE;

        showZoomModeDialog: boolean = false;
        selectedFormForZoom: IDocumentFormQA | null = null;
        allQuestions: IDocumentFormQA[] = [];
        currentQuestionIndex: number = 1;
        totalQuestions: number = 0;
        zoomModeActiveTabIndex = model<string>('0');

        isDownloading: boolean = false;
        searchQueryInAssignee: string = '';
        filteredUsers: IUser[] = [];

        showDocumentPreview: boolean = false;
        selectedDocumentFile: IAttachment | null = null;
        selectedDocumentId: string = '';

        //Bulk actions for a section
        selectedFormItems: Record<string, boolean> = {};
        selectAllSection: boolean = false;
        bulkSectionIndex: number | null = null
        isBulkDelete: boolean = false;

        //retry options
        retryOptions: {
                answerId: string,
                prompt: string, qnaBank: boolean, repository: boolean
        } = {
                        answerId: '', prompt: '', qnaBank: false, repository: false
                }

        uploadFileDialog = false;
        uploadDialogFiles: File[] = [];
        uploadFileDialogContext: {
                question: IFormQuestion | null,
                answer: IFormAnswer | null,
                allowedFileTypes: FileDropTypes[]
        } = {
                        question: null,
                        answer: null,
                        allowedFileTypes: [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC]
                }
        //Filters
        selectedFilter: IFormFilter = {
                assignedToMe: false,
                watchedByMe: false,
                questionStatuses: [],
                answerStatuses: [],
                authors: [],
                assignees: []
        }
        appliedFilter: {
                assignedToMe?: boolean,
                watchedByMe?: boolean,
                questionStatuses?: {
                        label: string,
                        value: FORM_QUESTION_STATUS,
                        style: string
                }[],
                answerStatuses?: {
                        label: string,
                        value: ANSWER_GENERATION_STATUS,
                        style: string
                }[],
                authors?: {
                        label: string,
                        value: ANSWER_GENERATION_TYPE
                }[],
                assignees?: IUser[]
        } = {
                        assignedToMe: false,
                        watchedByMe: false,
                        questionStatuses: [],
                        answerStatuses: [],
                        authors: [],
                        assignees: []
                }
        questionFilterOptions = [
                {
                        label: 'Flagged',
                        value: FORM_QUESTION_STATUS.FLAGGED,
                        icon: 'pi pi-flag',
                        style: 'bg-yellow-100 text-yellow-800'
                },
                {
                        label: 'Rejected',
                        value: FORM_QUESTION_STATUS.REJECTED,
                        icon: 'pi pi-times',
                        style: 'bg-red-100 text-red-800'
                },
                {
                        label: 'Approved',
                        value: FORM_QUESTION_STATUS.APPROVED,
                        icon: 'pi pi-check-circle',
                        style: 'bg-green-100 text-green-800 ring-green-100'
                }
        ]
        answerFilterOptions = [
                {
                        label: 'Answered',
                        value: ANSWER_GENERATION_STATUS.EXTRACTED,
                        icon: 'pi pi-check',
                        style: 'bg-blue-100 text-blue-800'
                },
                {
                        label: 'Unanswered',
                        value: ANSWER_GENERATION_STATUS.NOT_EXTRACTED,
                        icon: 'pi pi-question-circle',
                        style: 'bg-gray-100 text-gray-800'
                }
        ]
        authorFilterOptions = [
                {
                        label: 'AI Generated',
                        value: ANSWER_GENERATION_TYPE.AI,
                        icon: 'pi pi-sparkles'
                },
                {
                        label: 'Team',
                        value: ANSWER_GENERATION_TYPE.USER,
                        icon: 'pi pi-users'
                }
        ]
        filterAssigneeSearchValue: string = ""
        filteredAssignees: IUser[] = []
        addingQuestion: boolean = false
        ANSWER_GENERATION_TYPE = ANSWER_GENERATION_TYPE;

        SHARE_ACCESS = SHARE_ACCESS;
        projectShareDetails: { users: IShareUser[], anyone: SHARE_ACCESS, projectShare?: boolean } = { users: [], anyone: SHARE_ACCESS.RESTRICTED, projectShare: false };
        projectDueDate: Date | undefined = undefined;
        savingAssignUserChanges : boolean = false


        private chatSidebarService = inject(ChatSidebarService);
        creditsState = inject(UserCreditsStateService);
        private userCreditsService = inject(UserCreditsService);

        constructor(
                private contractService: ContractService,
                private shareService: ShareContractService,
                private shareAccessService: ShareService,
                private socketService: SocketService,
                private documentFormService: DocumentFormService,
                private toastService: ToastService,
                private userService: UserService,
                private authService: AuthenticationService,
                private qnaPairService: QnAPairService,
                private ChangeDetectorRef: ChangeDetectorRef,
                private sharedSourcesService: SharedSourcesService,
                private questionSourceService: SharedSourcesService
        ) {
                this.authService.currentUser.subscribe(res => {
                        this.currentUser = res as IUser
                });

        }

        ngOnInit(): void {
                this.fetchDocumentForm();
                this.getPermissionOfUser();
                this.getUsers();
                // Ensure the credits signal is populated so the credits-overview
                // card can determine depleted vs ready state.
                this.creditsState.fetchCredits().subscribe();
                this.projectDueDate = this.contract?.project ? (this.contract?.project as IProject)?.dueDate ? new Date((this.contract?.project as IProject)?.dueDate) : undefined : undefined;
        }

        // Helper methods for type-safe multi-select access
        getMultiSelectAnswer(questionId: string): string[] {
                const answer = this.questionAnswers[questionId];
                return Array.isArray(answer) ? answer as string[] : [];
        }

        onMultiSelectChange(questionId: string, event: any): void {
                this.questionAnswers[questionId] = event.value;
        }

        get zoomMultiSelectAnswer(): string[] {
                if (!this.selectedFormForZoom?.question._id) return [];
                return this.getMultiSelectAnswer(this.selectedFormForZoom.question._id);
        }

        set zoomMultiSelectAnswer(value: string[]) {
                if (this.selectedFormForZoom?.question._id) {
                        this.questionAnswers[this.selectedFormForZoom.question._id] = value;
                }
        }

        searchQuery: string = '';

        onSearchChange(event: any): void {
                this.searchQuery = event.target.value;
        }

        clearSearch(): void {
                this.searchQuery = '';
        }

        getSectionFilteredForms(section: any): any[] {
                if (!this.searchQuery || this.searchQuery.trim().length === 0) {
                        return section.form || [];
                }

                return (section.form || []).filter((form: any) =>
                        form.question?.title?.toLowerCase().includes(this.searchQuery.toLowerCase())
                );
        }

        hasNoMatchingQuestions(): boolean {
                if (!this.documentForm?.sections || this.documentForm.sections.length === 0) return true;

                return this.documentForm.sections.every(
                        section => this.getSectionFilteredForms(section).length === 0
                );
        }

        ngOnChanges(changes: SimpleChanges): void {
                if (changes['currentTabIndex'] && !changes['currentTabIndex'].firstChange) {
                        if (this.filterMainOverlay) {
                                this.filterMainOverlay.hide();
                        }
                }
        }

        ngOnDestroy(): void {
                if (this.chatSidebarService.isOpen()) {
                        this.chatSidebarService.close();
                }
                if (this.documentFormQuestionExtractedSubscription) {
                        this.documentFormQuestionExtractedSubscription.unsubscribe();
                }
                if (this.documentFormAnswerExtractedSubscription) {
                        this.documentFormAnswerExtractedSubscription.unsubscribe();
                }
                if (this.documentFormStatusSubscription) {
                        this.documentFormStatusSubscription.unsubscribe();
                }
                if (this.answerGenerationStatusSubscription) {
                        this.answerGenerationStatusSubscription.unsubscribe();
                }
                if (this.documentForm?._id) {
                        this.emitSocketEvent(CLIENT_EVENTS.LEAVE_DOCUMENT_FORM_ROOM);
                        this.socketService.unregisterRoom('document_form_room');
                }
        }

        @ViewChild('assignUserOverlay') assignUserOverlay!: PopoverComponent;

        setupSocketListeners(): void {
                // Join the document form room
                if (this.documentForm?._id) {
                        this.emitSocketEvent(CLIENT_EVENTS.JOIN_DOCUMENT_FORM_ROOM);
                        this.socketService.registerRoom('document_form_room', CLIENT_EVENTS.JOIN_DOCUMENT_FORM_ROOM, this.documentForm._id);
                }

                // Listen for form questions extracted
                this.documentFormQuestionExtractedSubscription = this.socketService
                        .on<{ documentForm: string, sections: IDocumentForm['sections'] }>(SOCKET_EVENTS.DOCUMENT_FORM_QUESTION_EXTRACTED)
                        .subscribe(res => {
                                if (res && res[0]?.documentForm == this.documentForm._id) {
                                        if (res && res[0]?.sections) {

                                                if (!this.documentForm) {
                                                        this.documentForm = { sections: res[0].sections } as IDocumentForm;
                                                } else {
                                                        this.documentForm.sections = res[0].sections;
                                                }
                                                this.loading = false;
                                                this.initializeAnswers();
                                                this.formatOptionsForComponents();
                                                this.fetchFormMetrics();
                                        }
                                }
                        });

                // Listen for form answers extracted
                this.documentFormAnswerExtractedSubscription = this.socketService
                        .on<{ documentForm: string, answers: IFormAnswer[] }>(SOCKET_EVENTS.DOCUMENT_FORM_ANSWER_EXTRACTED)
                        .subscribe(res => {
                                if (res && res[0]?.documentForm == this.documentForm._id) {
                                        if (res && res[0]?.answers) {
                                                if (this.documentForm?.sections) {
                                                        this.updateAnswersFromSocket(res[0].answers);
                                                        this.fetchFormMetrics();
                                                }
                                        }
                                }
                        });

                this.documentFormStatusSubscription = this.socketService
                        .on<{ documentForm: string, status: IDocumentForm['status'] }>(SOCKET_EVENTS.DOCUMENT_FORM_STATUS_CHANGED)
                        .subscribe(res => {
                                if (res && res[0]?.documentForm == this.documentForm._id) {
                                        if (res && res[0]?.status) {
                                                this.documentForm.status = res[0].status;
                                                // A phase flipping to FAILED means a new exhaustion record was just
                                                // written server-side. Refresh credits so the exhaustion card appears
                                                // immediately (the cached signal is otherwise stale until a manual refresh).
                                                if (res[0].status?.questions === DOCUMENT_FORM_STATUS.FAILED
                                                        || res[0].status?.answers === DOCUMENT_FORM_STATUS.FAILED) {
                                                        this.creditsState.refreshCredits().subscribe();
                                                }
                                                this.fetchFormMetrics();
                                        }
                                }
                        });

                this.answerGenerationStatusSubscription = this.socketService
                        .on<{ documentForm: string, answer: IFormAnswer['_id'], status: IFormAnswer['status'] }>(SOCKET_EVENTS.ANSWER_GENERATION_STATUS_CHANGED)
                        .subscribe(res => {
                                if (res && res[0]?.documentForm == this.documentForm._id) {
                                        if (res && res?.[0]?.answer && res?.[0]?.status) {
                                                this.updateAnswerStatusFromSocket(res[0].answer, res[0].status);
                                                this.fetchFormMetrics();
                                        }
                                }
                        });
        }

        async emitSocketEvent(event: CLIENT_EVENTS.JOIN_DOCUMENT_FORM_ROOM | CLIENT_EVENTS.LEAVE_DOCUMENT_FORM_ROOM) {
                const socket = await this.socketService.getSocket();
                if (!socket) {
                        console.warn('Socket is not initialized yet:', event)
                        return
                }
                socket.emit(event, this.documentForm._id);
        }

        updateAnswersFromSocket(answers: IFormAnswer[]) {
                if (!this.documentForm?.sections) return;

                // Create a map of answers by questionId for quick lookup
                const answersMap = new Map<string, IFormAnswer>();
                answers.forEach(answer => {
                        answersMap.set(answer.question as string, answer);
                });

                //Create a map of times used of qna pairs
                const timesUsedMap = new Map<string, number>();
                answers.forEach(answer => {
                        answer.sources?.forEach(source => {
                                if (source.qnaPair?.timesUsed)
                                        timesUsedMap.set(source.qnaPair?._id as string, source.qnaPair?.timesUsed);
                        });
                });

                // Update the answers in the documentForm
                this.documentForm.sections.forEach(section => {
                        section.form.forEach(formItem => {
                                const questionId = (formItem.question as IFormQuestion)._id;
                                const answer = answersMap.get(questionId);
                                if (answer) {
                                        formItem.answer = answer;

                                        // Update the questionAnswers object based on question type
                                        const question = formItem.question as IFormQuestion;
                                        switch (question.type) {
                                                case QUESTION_TYPE.CHECKBOX:
                                                case QUESTION_TYPE.MULTIPLE_SELECT:
                                                        this.questionAnswers[questionId] = answer.value ? answer.value.split(',') : [];
                                                        formItem.answer.status = ANSWER_GENERATION_STATUS.EXTRACTED;
                                                        break;
                                                case QUESTION_TYPE.TABLE:
                                                        this.questionAnswers[questionId] = answer.cells && answer.cells.length > 0 ? answer.cells : question.cells ?? [];
                                                        break;
                                                case QUESTION_TYPE.ATTACHMENT:
                                                        this.questionAnswers[questionId] = answer.attachments ? answer.attachments : [];
                                                        break;
                                                default:
                                                        this.questionAnswers[questionId] = answer.value || '';
                                                        break;
                                        }
                                } else {
                                        (formItem.answer?.sources || [])?.forEach(source => {
                                                if (timesUsedMap.has(source.qnaPair?._id as string)) {
                                                        source.qnaPair.timesUsed = timesUsedMap.get(source.qnaPair?._id as string) ?? 0;
                                                }
                                        });
                                }
                        });
                });
                this.ChangeDetectorRef.detectChanges();
        }

        updateAnswerStatusFromSocket(answer: IFormAnswer['_id'], status: IFormAnswer['status']) {
                if (!this.documentForm?.sections) return;
                // Update the answers in the documentForm
                for (const section of this.documentForm.sections) {
                        for (const formItem of section.form) {
                                if (formItem.answer?._id == answer) {
                                        formItem.answer.status = status;
                                        break;
                                }
                        }
                }
        }

        fetchDocumentForm(filter: IFormFilter = {}) {
                const hasFilters = Object.keys(filter).length > 0 && !!this.documentForm;
                this.isApplyingFilters = hasFilters;
                this.loading = true;
                this.contractService.getDocumentForm(this.contract._id, filter).subscribe((res) => {
                        if (res && res.success) {
                                this.loading = false;
                                this.isApplyingFilters = false;

                                this.appliedFilter = {
                                        assignedToMe: this.selectedFilter.assignedToMe,
                                        watchedByMe: this.selectedFilter.watchedByMe,
                                        questionStatuses: [],
                                        answerStatuses: [],
                                        authors: [],
                                        assignees: this.users.filter(user => this.selectedFilter.assignees?.includes(user._id))
                                }
                                for (const status of (this.selectedFilter.questionStatuses || [])) {
                                        const statusData = this.questionFilterOptions.find(item => item.value == status)
                                        if (!statusData) {
                                                return;
                                        }
                                        this.appliedFilter.questionStatuses?.push({
                                                label: statusData?.label,
                                                value: statusData?.value,
                                                style: statusData?.style
                                        })
                                }
                                for (const status of (this.selectedFilter.answerStatuses || [])) {
                                        const statusData = this.answerFilterOptions.find(item => item.value == status)
                                        if (!statusData) {
                                                return;
                                        }
                                        this.appliedFilter.answerStatuses?.push({
                                                label: statusData?.label,
                                                value: statusData?.value,
                                                style: statusData?.style
                                        })
                                }
                                for (const status of (this.selectedFilter.authors || [])) {
                                        const authorData = this.authorFilterOptions.find(item => item.value == status)
                                        if (!authorData) {
                                                return;
                                        }
                                        this.appliedFilter.authors?.push({
                                                label: authorData?.label,
                                                value: authorData?.value
                                        })
                                }

                                this.documentForm = res.data;
                                if (!this.documentForm) return;
                                this.contract.dueDate = (this.documentForm.contract as IContract).dueDate;
                                this.totalQuestions = this.documentForm.sections.reduce((acc, section) => acc + section.form.length, 0);

                                if (!this.showZoomModeDialog) {
                                        this.currentQuestionIndex = 1;
                                }

                                let isDocOwner = false;
                                if (this.currentUser._id == this.contract.createdBy) {
                                        isDocOwner = true
                                }

                                for (const section of this.documentForm.sections) {
                                        for (const item of section.form) {
                                                const question = item.question as IFormQuestion;
                                                item.question['canViewWatchers'] = false
                                                if (isDocOwner || [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser.role)) {
                                                        item.question['canViewWatchers'] = true
                                                }
                                                if (((question.watchers as IUser[])?.map(watcher => watcher._id))?.includes(this.currentUser._id)) {
                                                        item.question['isWatcher'] = true
                                                }
                                                else {
                                                        item.question['isWatcher'] = false
                                                }
                                        }
                                }

                                this.setupSocketListeners();
                                this.initializeAnswers();
                                this.formatOptionsForComponents();
                                this.fetchFormMetrics(filter);

                                if (this.showZoomModeDialog && this.selectedFormForZoom) {
                                        this.refreshSelectedFormForZoom();
                                }
                        } else {
                                this.loading = false;
                                this.isApplyingFilters = false;
                        }
                });
        }

        fetchFormMetrics(filter: IFormFilter = this.selectedFilter) {
                this.documentFormService.formMetrics(this.documentForm._id, filter).subscribe((res) => {
                        if (res && res.success) {
                                this.metrics = res.data.metrics;
                        } else {
                                this.toastService.error('Failed to fetch form metrics');
                        }
                });
        }


        changeQuestionStatus(questionId: string, status: FORM_QUESTION_STATUS) {
                const data: { contract: IContract['_id'], documentForm: string, questionId: string, status: FORM_QUESTION_STATUS } = {
                        contract: this.contract._id,
                        documentForm: this.documentForm._id,
                        questionId: questionId,
                        status: status
                }
                this.documentFormService.changeQuestionStatus(data).subscribe((res) => {
                        if (res && res.success) {
                                this.updateQuestionStatusLocally(questionId, status);
                                this.fetchFormMetrics();
                                this.toastService.success('Status changed successfully');
                                if (this.showZoomModeDialog && this.selectedFormForZoom?.question._id === questionId) {
                                        this.refreshSelectedFormForZoom();
                                }
                        } else {
                                this.toastService.error(res.message);
                        }
                });
        }

        updateQuestionStatusLocally(questionId: string, status: FORM_QUESTION_STATUS) {
                if (this.documentForm?.sections) {
                        for (const section of this.documentForm.sections) {
                                for (const formItem of section.form) {
                                        if (formItem.question._id === questionId) {
                                                formItem.question.status = status;
                                                formItem.question.statusUpdatedBy = this.currentUser?._id;
                                                break;
                                        }
                                }
                        }
                }
        }

        updateQuestionWatcherStatusLocally(questionId: string, isWatcher: boolean) {
                if (this.documentForm?.sections) {
                        for (const section of this.documentForm.sections) {
                                for (const formItem of section.form) {
                                        if (formItem.question._id === questionId) {
                                                formItem.question.isWatcher = isWatcher;
                                                if (isWatcher) {
                                                        formItem.question.watchers = [...(formItem.question.watchers || []), this.currentUser] as IUser[];
                                                } else {
                                                        formItem.question.watchers = formItem.question.watchers?.filter(watcher => (watcher as IUser)._id !== this.currentUser?._id) as IUser[];
                                                }
                                                break;
                                        }
                                }
                        }
                }
        }

        updateBulkQuestionStatusLocally(questionIds: string[], status: FORM_QUESTION_STATUS) {
                if (this.documentForm?.sections) {
                        for (const section of this.documentForm.sections) {
                                for (const formItem of section.form) {
                                        if (questionIds.includes(formItem.question._id)) {
                                                formItem.question.status = status;
                                                formItem.question.statusUpdatedBy = this.currentUser?._id;
                                        }
                                }
                        }
                }
        }

        initializeAnswers() {

                if (this.documentForm?.sections) {
                        this.documentForm.sections.forEach((section: IDocumentForm['sections'][0]) => {
                                for (const form of section.form) {
                                        const question = form?.question as IFormQuestion;
                                        const answer = form?.answer as IFormAnswer;
                                        if (!question || !answer) {
                                                continue;
                                        }
                                        switch (question.type) {
                                                case QUESTION_TYPE.CHECKBOX:
                                                case QUESTION_TYPE.MULTIPLE_SELECT:
                                                        this.questionAnswers[question._id] = answer.value ? answer.value.split(',') : question.cells ?? [];
                                                        break;

                                                case QUESTION_TYPE.SINGLE_SELECT:
                                                case QUESTION_TYPE.RADIO:
                                                        // For select options, ensure the answer exists in the options array
                                                        if (answer.value && question.options) {
                                                                // Find if the answer value exists in the options
                                                                const optionExists = question.options.some(opt => opt.value === answer.value);
                                                                if (!optionExists) {
                                                                        answer.value = '';
                                                                        this.questionAnswers[question._id] = '';
                                                                } else {
                                                                        this.questionAnswers[question._id] = answer.value;
                                                                }
                                                        } else {
                                                                this.questionAnswers[question._id] = answer.value || '';
                                                        }
                                                        break;




                                                case QUESTION_TYPE.TABLE:
                                                        this.questionAnswers[question._id] = answer.cells && answer.cells.length > 0 ? answer.cells : question.cells ?? [];
                                                        break;
                                                case QUESTION_TYPE.ATTACHMENT:
                                                        this.questionAnswers[question._id] = answer.attachments ? answer.attachments : [];
                                                        break;
                                                case QUESTION_TYPE.TEXT:
                                                default:
                                                        this.questionAnswers[question._id] = answer.value || '';
                                                        break;
                                        }
                                }
                        });
                }
                this.originalAnswers = structuredClone(this.questionAnswers);
        }

        formatOptionsForComponents() {
                if (this.documentForm?.sections) {
                        this.documentForm.sections.forEach((section: IDocumentForm['sections'][0]) => {
                                section.form.forEach((form: { question: string | IFormQuestion, answer: string | IFormAnswer }) => {
                                        const question = form.question as IFormQuestion;
                                        if (question.options && question.options.length > 0) {
                                                this.formattedOptions[question._id] = question.options.map(option => ({
                                                        label: option.label,
                                                        value: option.value
                                                }));
                                        } else {
                                                this.formattedOptions[question._id] = [];
                                        }
                                });
                        });
                }
        }

        // Handle changes for all question types
        onAnswerChange(form: { question: IFormQuestion, answer: IFormAnswer }) {
                let originalValue = '';
                if (Array.isArray(this.originalAnswers[form.question._id])) {
                        // For checkbox and multiselect, join values
                        originalValue = (this.originalAnswers[form.question._id] as string[]).join(',');
                } else {
                        // For text, single select, radio
                        originalValue = String(this.originalAnswers[form.question._id] || '');
                }

                let newValue = '';
                if (Array.isArray(this.questionAnswers[form.question._id])) {
                        // For checkbox and multiselect, join values
                        newValue = (this.questionAnswers[form.question._id] as string[]).join(',');
                } else {
                        // For text, single select, radio
                        newValue = String(this.questionAnswers[form.question._id] || '');
                }

                if (originalValue !== newValue) {
                        const formAnswer = form.answer;

                        // Handle different answer types
                        if (Array.isArray(formAnswer.value)) {
                                // For checkbox and multiselect, join values
                                formAnswer.value = formAnswer.value.join(',');
                        } else {
                                // For text, single select, radio
                                formAnswer.value = String(formAnswer.value || '');
                        }
                        this.saveAnswer(form);
                }
        }

        private isAnswerUnchanged(question: IFormQuestion, answer: IFormAnswer, newValue: string, cells: string[][]): boolean {
                if (question.type === QUESTION_TYPE.TABLE) {
                        const originalCells = (this.originalAnswers[question._id] as string[][]) ?? answer.cells ?? [];
                        if (!Array.isArray(originalCells) || originalCells.length !== cells.length) return false;
                        return originalCells.every((row: string[], i: number) =>
                                Array.isArray(row) && row.length === (cells[i]?.length ?? 0) &&
                                row.every((cell: string, j: number) => String(cell ?? '') === String(cells[i]?.[j] ?? ''))
                        );
                }
                const original = this.originalAnswers[question._id];
                let originalStr = '';
                if (Array.isArray(original)) {
                        originalStr = (original as string[]).join(',');
                } else {
                        originalStr = String(original ?? answer.value ?? '');
                }
                return originalStr === newValue;
        }

        saveAnswer(form: { question: IFormQuestion, answer: IFormAnswer }) {
                const question = form.question;
                const answer = form.answer;


                let newValue = this.questionAnswers[question._id];
                let originalValue = newValue; // Preserve original value to display in UI
                let cells = question.type === QUESTION_TYPE.TABLE ?
                        (Array.isArray(this.questionAnswers[question._id]) &&
                                (this.questionAnswers[question._id] as string[][]).every((row: string[]) => Array.isArray(row) && row.every((cell: string) => typeof cell === 'string'))
                                ? this.questionAnswers[question._id] as string[][]
                                : [['', '', ''], ['', '', ''], ['', '', '']])
                        : [];

                switch (question.type) {
                        case QUESTION_TYPE.SINGLE_SELECT:
                        case QUESTION_TYPE.RADIO:
                        case QUESTION_TYPE.TEXT:
                                newValue = String(newValue || '');
                                break;
                        case QUESTION_TYPE.MULTIPLE_SELECT:
                        case QUESTION_TYPE.CHECKBOX:

                                if (Array.isArray(newValue)) {
                                        newValue = newValue.join(',');
                                } else {
                                        newValue = String(newValue || '');
                                }
                                break;
                        default:
                                newValue = String(newValue || '');
                                break;
                }

                if (this.isAnswerUnchanged(question, answer, newValue, cells)) {
                        return;
                }

                const data = {
                        contract: this.contract._id,
                        documentForm: this.documentForm._id,
                        answerId: answer._id,
                        value: newValue,
                        cells: cells,
                        type: question.type
                };

                this.documentFormService.changeAnswerValue(data).subscribe((res) => {
                        if (res && res.success) {
                                // this.fetchDocumentForm();
                                if (question.type === QUESTION_TYPE.TABLE) {
                                        this.questionAnswers[question._id] = cells;
                                        answer.cells = cells as string[][];
                                }
                                else if (question.type === QUESTION_TYPE.MULTIPLE_SELECT || question.type === QUESTION_TYPE.CHECKBOX) {
                                        // For MULTIPLE_SELECT and CHECKBOX, preserve the original array value for UI
                                        this.questionAnswers[question._id] = originalValue;
                                        answer.value = newValue; // Use the string value for backend
                                }
                                else {
                                        this.questionAnswers[question._id] = newValue;
                                        answer.value = newValue;
                                }

                                if (answer.answeredBy) {
                                        answer.answeredBy = ANSWER_GENERATION_TYPE.USER;
                                }

                                this.originalAnswers = structuredClone(this.questionAnswers);
                                this.toastService.success('Answer saved successfully');
                        } else {
                                this.toastService.error(res.message || 'Failed to save answer');
                        }
                });
        }

        // Helper method to get proper CSS classes for accordion headers
        getAccordionHeaderClass(form: { question: IFormQuestion, answer: IFormAnswer }): { [key: string]: boolean } {
                return {
                        'answered-question': form?.answer?.status == ANSWER_GENERATION_STATUS.EXTRACTED,
                        'unanswered-question': form?.answer?.status == ANSWER_GENERATION_STATUS.PENDING,
                        'flagged-question': form?.question?.status == FORM_QUESTION_STATUS.FLAGGED,
                        'rejected-question': form?.question?.status == FORM_QUESTION_STATUS.REJECTED,
                        'approved-question': form?.question?.status == FORM_QUESTION_STATUS.APPROVED,
                        'diagonal-gradient-animation': form?.answer?.status == ANSWER_GENERATION_STATUS.IN_PROGRESS,
                        'highlighted-question': this.currentActiveQuestionId == form?.question?._id
                };
        }

        // Get formatted display value for an answer
        getDisplayValue(question: IFormQuestion): string {
                const answer = this.questionAnswers[question._id];

                if (!answer) return '';

                if (Array.isArray(answer)) {
                        // For array values (CHECKBOX, MULTIPLE_SELECT), get the labels from options
                        if (question.options && question.options.length > 0) {
                                return answer.map(value => {
                                        const option = question.options.find(opt => opt.value === value);
                                        return option ? option.label : value;
                                }).join(', ');
                        }
                        return answer.join(', ');
                } else if (answer instanceof Date) {
                        return answer.toLocaleDateString();
                } else if (question.type === QUESTION_TYPE.SINGLE_SELECT || question.type === QUESTION_TYPE.RADIO) {
                        // For single select, find the matching option to display the label
                        const option = question.options.find(opt => opt.value === answer);
                        return option ? option.label : answer.toString();
                } else {
                        return answer.toString();
                }
        }

        // Check if a question has selected options for array-based answers
        hasSelectedOptions(questionId: string): boolean {
                const answer = this.questionAnswers[questionId];
                return Array.isArray(answer) && answer.length > 0;
        }

        // Find label for a given value
        getOptionLabel(question: IFormQuestion, value: string): string {
                if (!question.options) return value;

                const option = question.options.find(opt => opt.value === value);
                return option ? option.label : value;
        }

        flagQuestion(question: IFormQuestion) {
                let status = FORM_QUESTION_STATUS.FLAGGED;
                // If the question is already flagged, set it to pending
                if (question.status == FORM_QUESTION_STATUS.FLAGGED) {
                        status = FORM_QUESTION_STATUS.PENDING;
                } else {
                        status = FORM_QUESTION_STATUS.FLAGGED
                }
                this.changeQuestionStatus(question._id, status);
        }

        @ViewChild('retry') retryOverlay!: PopoverComponent

        retryQuestion(event: Event, answerId: string) {
                this.retryOptions = {
                        answerId: answerId,
                        prompt: '',
                        qnaBank: true,
                        repository: false
                }
                this.retryOverlay.toggle(event)
        }

        /** True when question extraction was interrupted by credits exhaustion. */
        get questionsFailed(): boolean {
                return this.documentForm?.status?.questions === DOCUMENT_FORM_STATUS.FAILED;
        }

        /** True when answer generation was interrupted by credits exhaustion. */
        get answersFailed(): boolean {
                return this.documentForm?.status?.answers === DOCUMENT_FORM_STATUS.FAILED;
        }

        /**
         * Live agent-balance check, used to render the credits-exhausted screen instead of a
         * queue/progress screen. Extraction blocked by a pre-flight check never started a
         * thread, so there is no exhaustions[] row to look up — the balance is the only signal.
         */
        get agentCreditsExhausted(): boolean {
                return this.creditsState.agentExhausted();
        }

        /**
         * True when the full-panel credits screen owns the empty questionnaire, in which case the
         * header card would duplicate it. Only suppress the card here — once credits are topped up
         * this turns false again so the card can offer Resume on a form with nothing extracted.
         */
        get showExtractionExhaustedScreen(): boolean {
                return !this.documentForm?.sections?.length && this.agentCreditsExhausted;
        }

        /**
         * Pre-flight agent-credits check for the LLM calls in this component.
         * agentExhausted() is false while credits are still loading, so a slow
         * /credits fetch never produces a false "out of credits" block.
         */
        private hasAgentCredits(): boolean {
                if (this.creditsState.agentExhausted()) {
                        this.toastService.error("You've run out of credits. Request more to continue.");
                        return false;
                }
                const remaining = this.creditsState.agentRemaining();
                if (remaining > 0 && remaining < 3) {
                        this.toastService.warn(`Low credits (${remaining} remaining). This task may not complete.`);
                }
                return true;
        }

        /**
         * Resume question extraction after credits are topped up (Case 1).
         * Removes the exhaustion record, refreshes the credits signal (hides the
         * card), and re-triggers extraction via the existing endpoint.
         */
        onResumeExtraction(): void {
                if (!this.documentForm?._id) return;
                if (!this.hasAgentCredits()) return;
                const formId = this.documentForm._id;
                this.userCreditsService.removeExhaustion('QUESTION_EXTRACTION', formId).subscribe({
                        next: () => {
                                this.creditsState.refreshCredits().subscribe();
                                this.documentFormService.extractQuestions(formId).subscribe({
                                        next: () => {
                                                this.toastService.success('Question extraction resumed');
                                                this.fetchDocumentForm(this.selectedFilter);
                                        },
                                        error: () => {
                                                this.toastService.error('Failed to resume extraction');
                                        },
                                });
                        },
                        error: () => {
                                this.toastService.error('Failed to remove exhaustion record');
                        },
                });
        }

        /**
         * Called after the CreditsOverviewComponent resumes the paused answering
         * thread via POST /thread/resume (which already handles exhaustion removal
         * and restarts the same thread in Python).  This handler only needs to
         * refresh the credits signal and re-fetch the form to reflect the new
         * IN_PROGRESS status.
         */
        onResumeAnswering(): void {
                this.creditsState.refreshCredits().subscribe();
                this.toastService.success('Answer generation resumed');
                this.fetchDocumentForm(this.selectedFilter);
        }

        generateRetry() {
                if (!this.hasAgentCredits()) return;
                if (this.bulkSectionIndex == null) {
                        this.documentFormService.retryAnswer(this.documentForm._id, this.retryOptions).subscribe((res) => {
                                if (res.success) {
                                        this.toastService.success('Retry answer started');
                                        this.retryOverlay.hide();
                                }
                        });
                }
                else {
                        if (!this.atleastOneQuestionSelected()) {
                                return;
                        }
                        const questionIds: string[] = []
                        const section = this.documentForm.sections[this.bulkSectionIndex]
                        for (const form of section.form) {
                                if (this.selectedFormItems[form._id]) {
                                        questionIds.push(form.question._id)
                                }
                        }
                        this.documentFormService.bulkRetryAnswers(this.documentForm._id, section._id, questionIds, {
                                prompt: this.retryOptions.prompt,
                                qnaBank: this.retryOptions.qnaBank,
                                repository: this.retryOptions.repository
                        }).subscribe(res => {
                                if (res.success) {
                                        this.toastService.success('Retry answers started');
                                        this.retryOverlay.hide();
                                        this.unselectBulkSection()
                                }
                        })
                }
        }

        approveQuestion(question: IFormQuestion) {
                this.changeQuestionStatus(question._id, FORM_QUESTION_STATUS.APPROVED);
        }

        rejectQuestion(question: IFormQuestion) {
                this.changeQuestionStatus(question._id, FORM_QUESTION_STATUS.REJECTED);
        }

        getPermissionOfUser() {
                return new Promise((resolve, reject) => {
                        this.isSameOrganization = this.currentUser?.organization === this.contract.organization
                        if (!this.isSameOrganization || this.currentUser?.anonymous) {
                                this.permissions = {
                                        canEdit: false,
                                        canComment: false,
                                        canView: true
                                }
                                this.isEditor = false
                                this.currentRole = CONTRACT_USER_ROLE.VIEWER
                                this.isOwner = false
                                resolve(true)
                                return;
                        }
                        this.shareService.getPermissionOfUser(this.contract._id as string, SHARE_FEATURE.CONTRACT).subscribe(res => {
                                if (res.success) {
                                        this.permissions = res.data
                                        if (this.permissions.canEdit) {
                                                this.isEditor = true
                                                this.currentRole = CONTRACT_USER_ROLE.EDITOR
                                        } else if (this.permissions.canComment) {
                                                this.currentRole = CONTRACT_USER_ROLE.COMMENTER
                                        } else if (this.permissions.canView) {
                                                this.currentRole = CONTRACT_USER_ROLE.VIEWER
                                        }
                                        if (this.contract.createdBy == this.currentUser?._id) {
                                                this.isOwner = true
                                                this.currentRole = CONTRACT_USER_ROLE.OWNER
                                        }
                                        resolve(true)
                                } else {
                                        reject('Failed to fetch role');
                                }
                        })
                })
        }

        updateQuestionTitle(event: Event, form: { question: IFormQuestion, answer: IFormAnswer }) {
                event.preventDefault()
                const target = event.target as HTMLElement;
                const newTitle = target.textContent?.trim() || 'Untitled';
                target.innerHTML = newTitle

                if (form.question.title !== newTitle) {
                        form.question.title = newTitle;
                        this.documentFormService.updateQuestionTitle({
                                contract: this.contract._id,
                                documentForm: this.documentForm._id,
                                questionId: form.question._id,
                                title: newTitle
                        }).subscribe((res) => {
                                if (res && res.success) {
                                        this.toastService.success('Question title updated successfully');
                                } else {
                                        this.toastService.error(res.message);
                                }
                        })
                }
        }

        activateEditing(event: Event, form: { question: IFormQuestion, answer: IFormAnswer }) {
                event.preventDefault();
                event.stopPropagation();
                const target = event.target as HTMLElement;
                const parentDiv = target.parentElement;
                const editableSpan = parentDiv?.querySelector('[contenteditable]') as HTMLElement;

                if (editableSpan && (this.isOwner || (this.isSameOrganization && this.permissions.canEdit))) {
                        editableSpan.focus();

                        // Select all text in the span
                        const range = document.createRange();
                        const selection = window.getSelection();
                        range.selectNodeContents(editableSpan);
                        selection?.removeAllRanges();
                        selection?.addRange(range);
                }
        }

        blurSpan(event: Event) {
                const target = event.target as HTMLElement;
                target.contentEditable = 'false';
        }

        copyAnswerToClipboard(question: IFormQuestion) {
                const textToCopy = this.getDisplayValue(question);
                if (textToCopy) {
                        navigator.clipboard.writeText(textToCopy).then(() => {
                                this.toastService.success('Answer copied to clipboard');
                        }).catch(() => {
                                this.toastService.error('Failed to copy to clipboard');
                        })
                } else {
                        this.toastService.warn('No answer to copy');
                }
        }

        getTableCells(question: IFormQuestion) {
                return this.questionAnswers[question._id] as string[][];
        }

        getTableCellValue(questionId: string, rowIndex: number, cellIndex: number): string {
                const tableData = this.questionAnswers[questionId] as string[][];
                return tableData?.[rowIndex]?.[cellIndex] || '';
        }

        onTableUpdate(event: { cells: string[][] }, form: { question: IFormQuestion, answer: IFormAnswer }) {
                this.questionAnswers[form.question._id] = event.cells;
        }

        setTableCellValue(questionId: string, rowIndex: number, cellIndex: number, value: string): void {
                const tableData = this.questionAnswers[questionId] as string[][];
                if (tableData && tableData[rowIndex]) {
                        tableData[rowIndex][cellIndex] = value;
                }
        }
        assignUserToQuestion(question: IFormQuestion, event: Event) {
                this.unselectBulkSection()
                this.selectedQuestion = question;
                this.questionApprovers = [...(question.assignedTo as { user: IUser }[]).map(user => ({
                        user: user.user,
                        email: user.user.email,
                        name: user.user.fullName || user.user.email.split('@')[0],
                        type: USER_TYPE.INTERNAL
                }))];

                this.searchQueryInAssignee = '';
                this.filteredUsers = [...this.users];
                this.openApproverDialog(question);
                this.assignUserOverlay.toggle(event);
        }

        openApproverDialog(question: any) {
                this.onCloseApproverDialog();

                this.selectedQuestion = question;
                const currentApprovers = question.assignedTo || [];
                this.questionApprovers = [...currentApprovers];
                this.tempQuestionApprovers = [...currentApprovers];
                this.originalQuestionApprovers = [...currentApprovers];
                this.tempQuestionApproversSet = new Set(currentApprovers.map((approver: any) => (approver.user as IUser)._id));
                this.assignUserToQuestionDialog = true;
        }

        autoCompleteUsersMethod(event: any) {
                this.autoCompleteUsers = this.users.filter((user: IUser) => {
                        const fullName = user.fullName || '';
                        const email = user.email || '';
                        return (fullName.toLowerCase().includes(event.query.toLowerCase()) ||
                                email.toLowerCase().includes(event.query.toLowerCase()));
                });
        }

        getShareDetails() {
                this.shareService.getShareDetails(this.contract._id, SHARE_FEATURE.CONTRACT).subscribe((res) => {
                        if (res.success) {
                                this.shareDetails = res.data;
                        }
                });
        }

        getProjectShareDetails() {
                this.shareService.getShareDetails(this.projectId, SHARE_FEATURE.PROJECT).subscribe((res) => {
                        if (res.success) {
                                this.projectShareDetails = res.data;
                                this.filterAssignees();
                        }
                });
        }

        filterAssignees() {
                this.userService.getAssignees(this.contract._id).subscribe((res) => {
                        if (res.success) {
                                this.filteredAssignees = res.data;
                        }
                });
        }

        getUsers() {
                this.userService.getUsers().subscribe((res) => {
                        if (res && res.success) {
                                this.users = res.data.users;
                                this.getShareDetails();
                                this.getProjectShareDetails();
                        } else {
                                this.toastService.error('Failed to fetch users');
                        }
                });
        }

        addInternalApprover(event: any) {
                const user = event.value;
                const findUser = this.questionApprovers.find((approver: IShareUser) => approver.email === user.email);
                if (findUser) {
                        this.toastService.warn('User already added');
                        this.autoCompleteValue = '';
                        return;
                }
                this.questionApprovers.push({
                        name: user.fullName,
                        user: user,
                        email: user.email,
                        type: USER_TYPE.INTERNAL
                });
                this.autoCompleteValue = '';
        }

        removeApprover(index: number) {
                this.questionApprovers.splice(index, 1);
        }

        editApproverFunc(index: number) {
                this.editApprover[index] = !this.editApprover[index];
        }

        dragStart(index: number) {
                this.draggedApproverIdx = index;
        }

        drop(dropIndex: number) {
                if (this.draggedApproverIdx !== undefined && this.draggedApproverIdx !== null && this.draggedApproverIdx !== dropIndex) {
                        const draggedApprover = this.questionApprovers[this.draggedApproverIdx];
                        this.questionApprovers.splice(this.draggedApproverIdx, 1);
                        this.questionApprovers.splice(dropIndex, 0, draggedApprover);
                        this.draggedApproverIdx = null;
                }
        }

        isUserUnverified(item: IShareUser): boolean {
                if (!item || !item.user || typeof item.user === 'string') {
                        return false;
                }
                const user = item.user as IUser;
                return user.verified === false;
        }

        isWatcherDeactivated(watcher: string | IUser): boolean {
                if (!watcher || typeof watcher === 'string') {
                        return false;
                }
                const user = watcher as IUser;
                return user.deactivationStatus === DEACTIVATION_STATUS.DEACTIVATED;
        }

        checkIfAssigner(question: IFormQuestion): boolean {
                return this.isOwner || question.assignedTo.some((user: { user: IUser }) => user.user._id === this.currentUser._id);
        }

        actionsCommenterCanPerform() {
                return this.isOwner || this.permissions.canEdit || this.permissions.canComment;
        }

        canEdit() {
                return this.isOwner || this.permissions.canEdit;
        }

        canComment() {
                return this.isOwner || this.isEditor || this.permissions.canComment;
        }

        downloadDocument() {
                if (!this.contract?._id) return;

                this.isDownloading = true;
                this.documentFormService.downloadForm(this.contract._id)
                        .subscribe({
                                next: (res) => {
                                        let uint8Array: any = new Uint8Array(res.data.data);
                                        downloadDocument(uint8Array, `${this.contract.name}-form`, 'application/pdf');
                                },
                                error: (err) => {
                                        console.error('Error downloading document:', err);
                                },
                                complete: () => {
                                        this.isDownloading = false;
                                }
                        });
        }

        openAddQuestionDialog(section: IDocumentForm['sections'][0]) {
                this.showAddQuestionDialog = true;
                this.selectedSection = section._id;
        }

        addQuestion() {
                if (!this.questionData.title || !this.questionData.type) {
                        this.toastService.error('Please fill in all required fields');
                        return;
                }

                if (this.questionData.type === QUESTION_TYPE.SINGLE_SELECT || this.questionData.type === QUESTION_TYPE.MULTIPLE_SELECT || this.questionData.type === QUESTION_TYPE.CHECKBOX || this.questionData.type === QUESTION_TYPE.RADIO) {
                        if (!this.questionData.options || this.questionData.options.length === 0) {
                                this.toastService.error('Please add at least one option');
                                return;
                        }
                }
                else {
                        this.questionData.options = []
                }

                if (this.questionData.type != QUESTION_TYPE.TABLE) {
                        this.questionData.cells = []
                }
                // A newly added question gets an AI answer generated server-side.
                if (!this.hasAgentCredits()) return;
                this.addingQuestion = true;
                this.documentFormService.addQuestion(this.documentForm._id, this.selectedSection, this.questionData).subscribe((res) => {
                        if (res.success) {
                                this.toastService.success('Question added successfully');
                                this.closeAddQuestionDialog();
                                this.fetchDocumentForm(this.selectedFilter);
                        }
                        this.addingQuestion = false;
                })
        }

        closeAddQuestionDialog() {
                this.showAddQuestionDialog = false;
                this.questionData = {};
        }

        addOption() {
                this.questionData.options = [...(this.questionData.options || []), { label: '', value: '' }];
        }

        removeOption(index: number) {
                this.questionData.options = this.questionData.options?.filter((_, i) => i !== index);
        }

        private isValidDate(dateString: string): boolean {
                const date = new Date(dateString);
                return date instanceof Date && !isNaN(date.getTime());
        }

        askAI(general: boolean = false) {
                if (general) {
                        this.selectedQuestionForChat = null;
                }
                this.chatSidebarService.open('qna', {
                        contractId: this.contract._id,
                        documentTypeId: this.contract.linkedDocType?._id,
                        formQuestions: this.allFormQuestions,
                        selectedFormQuestionId: this.selectedQuestionForChat?.value ?? null,
                });
        }

        openDocumentPreview(constructionAttachment: IConstructionAttachment) {
                if (constructionAttachment && constructionAttachment._id) {
                        const file = constructionAttachment.attachment as IAttachment;
                        this.selectedDocumentFile = file as IAttachment;
                        this.selectedDocumentFile.fullUrl = file.url;
                        this.selectedDocumentId = file._id; // attachment id
                        this.selectedDocumentFile.filename = file.filename;
                        this.showDocumentPreview = true;
                }
        }

        closeDocumentPreview() {
                this.showDocumentPreview = false;
                this.selectedDocumentFile = null;
                this.selectedDocumentId = '';
        }

        updateLinkedQnAPairLocally(questionId: string, qnaPair: IQnaPair) {
                if (this.documentForm?.sections) {
                        for (const section of this.documentForm.sections) {
                                for (const formItem of section.form) {
                                        if (formItem.question._id === questionId) {
                                                formItem.question.linkedQnAPair = qnaPair;
                                                break;
                                        }
                                }
                        }
                }
        }

        addQuestionToQnA(question: IFormQuestion, answer: IFormAnswer) {
                this.qnaPairService.addQuestionToQnA(this.contract._id, question._id, answer._id).subscribe((res) => {
                        if (res.success) {
                                this.toastService.success('Question added to QnA');
                                this.updateLinkedQnAPairLocally(question._id, res.data);
                                if (this.showZoomModeDialog && this.selectedFormForZoom?.question._id === question._id) {
                                        this.refreshSelectedFormForZoom();
                                }
                        } else {
                                this.toastService.error(res.message);
                        }
                });
        }

        public selectedQuestionForChat: { label: string, value: string, data: any } | null = null;
        openFloatingChat(form: { question: IFormQuestion, answer: IFormAnswer }) {
                this.selectedQuestionForChat = {
                        label: form.question.title,
                        value: form.question._id,
                        data: form
                };
                this.askAI();
        }

        get allFormQuestions(): { _id: string; name: string; sectionTitle?: string }[] {
                if (!this.documentForm?.sections) return [];
                return this.documentForm.sections.flatMap(section =>
                        section.form.map(f => ({
                                _id: (f.question as IFormQuestion)._id,
                                name: (f.question as IFormQuestion).title,
                                sectionTitle: section.title,
                        }))
                );
        }

        @Output() toggleQuestionPanel = new EventEmitter<{ showPanel: boolean, activeTabIndex: number }>();

        openQuestionPanel(questionId: string, sources: IFormAnswer['sources'], activeTabIndex: number = 0) {
                this.questionSourceService.updateSources({ questionId: questionId, sources: sources || [] });
                this.toggleQuestionPanel.emit({ showPanel: true, activeTabIndex: activeTabIndex });
        }

        get currentActiveQuestionId() {
                return this.questionSourceService.currentQuestionId;
        }

        openWatcherOverlay(event: MouseEvent, question: IFormQuestion) {
                console.log("openWatcherOverlay", question);
                this.selectedQuestion = question
                this.watcherOverlay.toggle(event)
        }

        closeWatcherOverlay() {
                this.watcherOverlay.hide()
                this.selectedQuestion = null
        }

        updateWatchers() {
                console.log("updateWatchers", this.selectedQuestion);
                const question = this.selectedQuestion
                if (!question) {
                        return;
                }
                this.documentFormService.updateWatchers({ contractId: this.contract._id, questionId: question._id, add: !question.isWatcher }).subscribe(res => {
                        if (res.success) {
                                this.closeWatcherOverlay()
                                this.toastService.success(`You've been ${!question.isWatcher ? "added" : "removed"} as a watcher.`)
                                this.updateQuestionWatcherStatusLocally(question._id, !question.isWatcher);
                                if (this.showZoomModeDialog && this.selectedFormForZoom?.question._id === question._id) {
                                        this.refreshSelectedFormForZoom();
                                }
                        }
                })
        }

        selectedSectionToBeDeleted: string = ""
        deleteTargetType: DELETE_TYPE | null = null;
        selectedQuestionToBeDeleted: string = "";
        selectedAnswerToBeDeleted: string = ""
        deleteDialogVisible = false;

        onDeleteConfirm() {
                if (this.deleteTargetType === DELETE_TYPE.SECTION) {
                        let sectionId = ""
                        if (this.isBulkDelete && (this.bulkSectionIndex != null)) {
                                sectionId = this.documentForm.sections[this.bulkSectionIndex]._id
                        }
                        else {
                                sectionId = this.selectedSectionToBeDeleted
                        }
                        this.deleteSection(sectionId);
                } else if (this.deleteTargetType === DELETE_TYPE.QUESTION) {
                        const questionIds = [], answerIds = []
                        if (this.isBulkDelete && (this.bulkSectionIndex != null)) {
                                const section = this.documentForm.sections[this.bulkSectionIndex]
                                for (const form of section.form) {
                                        if (this.selectedFormItems[form._id]) {
                                                questionIds.push(form.question._id)
                                                answerIds.push(form.answer._id)
                                        }
                                }
                        }
                        else {
                                questionIds.push(this.selectedQuestionToBeDeleted)
                                answerIds.push(this.selectedAnswerToBeDeleted)
                        }
                        this.deleteQuestion(questionIds, answerIds);
                }

                this.deleteDialogVisible = false;
                this.deleteTargetType = null;
        }

        onDeleteSectionPanel(section: IDocumentFormSection) {
                this.isBulkDelete = false
                this.deleteTargetType = DELETE_TYPE.SECTION;
                this.selectedSectionToBeDeleted = section._id;
                this.deleteDialogVisible = true;
        }

        onDeleteQuestionPanel(form: IDocumentFormQA) {
                this.isBulkDelete = false
                this.deleteTargetType = DELETE_TYPE.QUESTION;
                this.selectedQuestionToBeDeleted = form.question._id;
                this.selectedAnswerToBeDeleted = form.answer._id;
                this.deleteDialogVisible = true;
        }


        deleteSection(section: string) {
                const sectionId = section;
                const documentFormId = this.documentForm._id;
                this.documentFormService.deleteSection(documentFormId, sectionId).subscribe((res) => {
                        if (res.success) {
                                this.toastService.success(res.message)
                                this.fetchDocumentForm(this.selectedFilter)
                        }
                        else {
                                this.toastService.error(res.message)
                        }
                        this.deleteDialogVisible = false;
                        if (this.isBulkDelete) {
                                this.unselectBulkSection()
                        }
                })
        }

        deleteQuestion(questionIds: string[], answerIds: string[]) {
                this.documentFormService.deleteQnA(this.documentForm._id, questionIds, answerIds).subscribe((res) => {
                        if (res.success) {
                                this.toastService.success(res.message);
                                this.fetchDocumentForm(this.selectedFilter);
                        } else {
                                this.toastService.error(res.message);
                        }
                });

                this.deleteDialogVisible = false;
                if (this.isBulkDelete) {
                        this.unselectBulkSection()
                }
        }

        onBulkAction() {

        }

        initializeApproverDialog(question: any) {
                this.selectedQuestion = question;
                const currentApprovers = question.assignedTo || [];
                this.questionApprovers = [...currentApprovers];
                this.tempQuestionApprovers = [...currentApprovers];
                this.originalQuestionApprovers = [...currentApprovers];
                this.tempQuestionApproversSet = new Set(currentApprovers.map((approver: any) => (approver.user as IUser)._id));
                this.assignUserToQuestionDialog = true;
        }

        filterUsers() {
                if (!this.searchQueryInAssignee.trim()) {
                        this.filteredUsers = [...this.filteredAssignees];
                        return;
                }

                const query = this.searchQueryInAssignee.toLowerCase().trim();
                this.filteredUsers = this.filteredAssignees.filter((user: IUser) => {
                        const fullName = (user.fullName || '').toLowerCase();
                        const email = (user.email || '').toLowerCase();
                        return fullName.includes(query) || email.includes(query);
                });
        }

        getRemainingUsers(): IUser[] {
                const addedUserIds = this.tempQuestionApprovers.map(approver => (approver.user as IUser)._id);
                const usersToShow = this.searchQueryInAssignee.trim() ? this.filteredUsers : this.filteredAssignees;
                return usersToShow.filter(user => !addedUserIds.includes(user._id));
        }

        shareAccessDialog: boolean = false;
        needAccessUser: Partial<IUser> = {} as Partial<IUser>;


        userAccessChanged(event: { user: Partial<IUser>, shareUser: Partial<IShareUser>, hasAccess: boolean }) {
                const user = event.user
                const hasAccess = event.hasAccess
                if (hasAccess) {
                        this.tempQuestionApprovers.push({
                                name: user.fullName || user.email?.split('@')[0] || '',
                                user: user,
                                email: user.email || '',
                                role: event.shareUser.role || SHARE_ACCESS.VIEW,
                                type: USER_TYPE.INTERNAL
                        });
                        if (user._id) {
                                this.tempQuestionApproversSet.add(user._id);
                        }
                        this.needAccessUser = {}
                        this.shareAccessDialog = false
                }
        }


        addUserTemporary(user: IUser) {
                const isAlreadyAdded = this.tempQuestionApprovers.some(approver => (approver.user as IUser)._id === user._id);
                if (isAlreadyAdded) {
                        this.toastService.warn('User already added');
                        return;
                }

                this.shareAccessService.isShareUserHaveAccess(user, this.contract._id, SHARE_FEATURE.CONTRACT).subscribe(res => {
                        if (!res.data.hasAccess) {
                                this.shareAccessDialog = true
                                this.needAccessUser = user
                                return;
                        }
                        if (this.tempQuestionApproversSet.has(user._id)) {
                                return;
                        }
                        this.tempQuestionApproversSet.add(user._id);
                        this.tempQuestionApprovers.push({
                                name: user.fullName || user.email.split('@')[0],
                                user: user,
                                email: user.email,
                                type: USER_TYPE.INTERNAL
                        });
                })
        }

        removeUserTemporary(user: any) {
                const index = this.tempQuestionApprovers.findIndex(approver => (approver.user as IUser)._id === user?._id);
                if (index > -1) {
                        this.tempQuestionApprovers.splice(index, 1);
                        this.tempQuestionApproversSet.delete(user._id);
                }
        }

        onSaveChanges() {
                if (this.bulkSectionIndex != null) {
                        if (!this.atleastOneQuestionSelected()) {
                                return;
                        }
                        this.savingAssignUserChanges = true;
                        const questionIds: string[] = []
                        const section = this.documentForm.sections[this.bulkSectionIndex]
                        for (const form of section.form) {
                                if (this.selectedFormItems[form._id]) {
                                        questionIds.push(form.question._id)
                                }
                        }
                        this.documentFormService.bulkAssignUsersToQuestions({
                                contractId: this.contract._id,
                                questions: questionIds,
                                data: this.tempQuestionApprovers.map(approver => approver.user as IUser).map(user => ({
                                        user: user._id,
                                        status: FORM_QUESTION_STATUS.PENDING,
                                        email: user.email,
                                        name: user.fullName || user.email.split('@')[0],
                                        type: USER_TYPE.INTERNAL
                                }))
                        }).subscribe({
                                next: (res) => {
                                        this.assignUserOverlay.hide()
                                        if (res.success) {
                                                this.unselectBulkSection()
                                                this.toastService.success("Assigned users successfully")
                                                this.fetchDocumentForm(this.selectedFilter)
                                        }
                                },
                                error: () => {
                                        this.savingAssignUserChanges = false;
                                },
                                complete: () => {
                                        this.savingAssignUserChanges = false;
                                }
                        })
                }
                else {
                        this.savingAssignUserChanges = true;
                        this.questionApprovers = [...this.tempQuestionApprovers];
                        this.saveQuestionApprovers();
                }
        }

        onOverlayHide() {
                if (this.shareAccessDialog || this.needAccessUser) {
                        return;
                }
                this.questionApprovers = [];
                this.tempQuestionApprovers = []
                this.tempQuestionApproversSet.clear();
        }

        saveQuestionApprovers() {
                if (!this.selectedQuestion) {
                        this.savingAssignUserChanges = false;
                        return;
                }

                const approverUsers = this.questionApprovers.map(approver => approver.user as IUser);

                this.documentFormService.assignUsersToQuestion({
                        contract: this.contract._id,
                        documentForm: this.documentForm._id,
                        questionId: this.selectedQuestion._id,
                        users: approverUsers.map(user => ({
                                user: user._id,
                                status: FORM_QUESTION_STATUS.PENDING,
                                email: user.email,
                                name: user.fullName || user.email.split('@')[0],
                                type: USER_TYPE.INTERNAL
                        }))
                }).subscribe({
                        next: (res) => {
                                if (res && res.success) {
                                        this.toastService.success('Assignees updated successfully');

                                        const updatedAssignedTo = approverUsers.map(user => ({
                                                user: user,
                                                status: FORM_QUESTION_STATUS.PENDING,
                                                email: user.email,
                                                name: user.fullName || user.email.split('@')[0],
                                                type: USER_TYPE.INTERNAL
                                        }));

                                        this.selectedQuestion!.assignedTo = updatedAssignedTo;

                                        if (this.documentForm && this.documentForm.sections) {
                                                for (let sectionIndex = 0; sectionIndex < this.documentForm.sections.length; sectionIndex++) {
                                                        const section = this.documentForm.sections[sectionIndex];
                                                        if (section.form) {
                                                                const formIndex = section.form.findIndex(formItem =>
                                                                        formItem.question._id === this.selectedQuestion!._id
                                                                );
                                                                if (formIndex > -1) {
                                                                        this.documentForm.sections[sectionIndex].form[formIndex].question.assignedTo = updatedAssignedTo;
                                                                        break;
                                                                }
                                                        }
                                                }
                                        }
                                        if (this.showZoomModeDialog && this.selectedFormForZoom?.question._id === this.selectedQuestion?._id) {
                                                this.refreshSelectedFormForZoom();
                                        }

                                        this.onCloseApproverDialog();
                                } else {
                                        this.toastService.error(res.message || 'Failed to assign approvers');
                                }
                        },
                        error: () => {
                                this.savingAssignUserChanges = false;
                        },
                        complete: () => {
                                this.savingAssignUserChanges = false;
                        }
                });
        }

        onCloseApproverDialog() {
                this.assignUserOverlay.hide();
                this.assignUserToQuestionDialog = false;
                this.questionApprovers = [];
                this.searchQueryInAssignee = '';
                this.filteredUsers = [];
                this.editApprover = {};
                this.selectedQuestion = null;
                this.draggedApproverIdx = null;
                this.tempQuestionApproversSet.clear();
        }

        selectWholeSection(section: IDocumentForm['sections'][0]) {
                this.selectedFormItems = {}
                if (this.selectAllSection) {
                        for (const form of section.form) {
                                this.selectedFormItems[form._id] = true
                        }
                }
        }

        onBulkActionClicked(index: number, section: IDocumentForm['sections'][0]) {
                this.bulkSectionIndex = index;
                this.selectAllSection = true;
                this.selectWholeSection(section)
        }

        unselectBulkSection() {
                this.bulkSectionIndex = null
                this.selectAllSection = false
                this.selectedFormItems = {}
                this.isBulkDelete = false
        }

        formItemCheckChange(section: IDocumentForm['sections'][0]) {
                const checkedItems = Object.values(this.selectedFormItems).filter(item => item)
                if (checkedItems.length == section.form.length) {
                        this.selectAllSection = true
                }
                else {
                        this.selectAllSection = false
                }
        }

        atleastOneQuestionSelected() {
                if (!Object.values(this.selectedFormItems).filter(item => item).length) {
                        this.toastService.warn("Please select atleast one question")
                        return false
                }
                return true
        }

        bulkUpdateStatus(status: FORM_QUESTION_STATUS, section: IDocumentForm['sections'][0]) {
                if (!this.atleastOneQuestionSelected()) {
                        return;
                }
                const questionIds: string[] = []

                for (const form of section.form) {
                        if (this.selectedFormItems[form._id]) {
                                questionIds.push(form.question._id)
                        }
                }

                this.qnaPairService.bulkStatusUpdatesQnAPairs(this.contract._id, questionIds, status).subscribe(res => {
                        if (res.success) {
                                this.unselectBulkSection()
                                let statusType = ''

                                if (status === FORM_QUESTION_STATUS.FLAGGED) {
                                        const { questionsToFlag = [], questionsToUnflag = [] } = res.data;

                                        if (questionsToFlag.length > 0) {
                                                this.updateBulkQuestionStatusLocally(questionsToFlag, FORM_QUESTION_STATUS.FLAGGED);
                                        }

                                        if (questionsToUnflag.length > 0) {
                                                this.updateBulkQuestionStatusLocally(questionsToUnflag, FORM_QUESTION_STATUS.PENDING);
                                        }

                                        if (questionsToFlag.length > 0 && questionsToUnflag.length > 0) {
                                                this.toastService.success(`${questionsToFlag.length} question(s) flagged and ${questionsToUnflag.length} question(s) unflagged successfully`);
                                        } else if (questionsToFlag.length > 0) {
                                                this.toastService.success(`${questionsToFlag.length} question(s) flagged successfully`);
                                        } else if (questionsToUnflag.length > 0) {
                                                this.toastService.success(`${questionsToUnflag.length} question(s) unflagged successfully`);
                                        }
                                } else {
                                        switch (status) {
                                                case FORM_QUESTION_STATUS.APPROVED: {
                                                        statusType = "Approved"
                                                        break;
                                                }
                                                case FORM_QUESTION_STATUS.REJECTED: {
                                                        statusType = "Rejected"
                                                        break;
                                                }
                                        }
                                        this.toastService.success(`${statusType} question(s) successfully`)
                                        this.updateBulkQuestionStatusLocally(questionIds, status);
                                }

                                this.fetchFormMetrics();
                        }
                })
        }

        bulkAddQnaPairs(section: IDocumentForm['sections'][0]) {
                if (!this.atleastOneQuestionSelected()) {
                        return;
                }
                const questionIds: string[] = []
                const answerIds: string[] = []
                for (const form of section.form) {
                        if (this.selectedFormItems[form._id]) {
                                questionIds.push(form.question._id)
                                answerIds.push(form.answer._id)
                        }
                }
                this.qnaPairService.bulkAddQnaPairsFromDocument({
                        documentId: this.contract._id,
                        questions: questionIds,
                        answers: answerIds
                }).subscribe(res => {
                        if (res.success) {
                                this.unselectBulkSection()
                                this.toastService.success("Question(s) added to Q&A bank successfully")
                                this.fetchDocumentForm(this.selectedFilter)
                        }
                })
        }

        bulkDeleteClicked() {
                this.isBulkDelete = true
                if (this.selectAllSection) {
                        this.deleteTargetType = DELETE_TYPE.SECTION;
                        this.deleteDialogVisible = true;
                }
                else {
                        if (!this.atleastOneQuestionSelected()) {
                                return;
                        }
                        this.deleteTargetType = DELETE_TYPE.QUESTION;
                        this.deleteDialogVisible = true;
                }
        }

        openBulkAssignUsersOverlay(event: Event) {
                this.assignUserOverlay.toggle(event)
        }

        openAttachmentUploadDialog(question: IFormQuestion, answer: IFormAnswer) {
                this.uploadFileDialogContext.question = question;
                this.uploadFileDialogContext.answer = answer;
                this.uploadDialogFiles = [];
                this.uploadFileDialog = true;
        }

        onFileUpload(files: File | File[]) {
                const filesData = Array.isArray(files) ? files : [files];
                this.uploadDialogFiles = filesData;
                this.hideUploadFileDialog();
                const formData = new FormData();
                formData.append('contract', this.contract._id);
                formData.append('documentForm', this.documentForm._id);
                formData.append('questionId', this.uploadFileDialogContext.question?._id ?? '');
                formData.append('answerId', this.uploadFileDialogContext.answer?._id ?? '');
                if (filesData.length) {
                        filesData.forEach(curr => {
                                formData.append('attachments', curr);
                        })
                }

                this.documentFormService.updateAttachmentTypeQuestion(formData).subscribe((res) => {
                        if (res.success) {
                                this.toastService.success('Answer updated successfully')
                                this.updateAnswersFromSocket([res.data])
                        }
                })
        }

        removeAttachmentFromAnswer(question: IFormQuestion, answer: IFormAnswer, attachment: IAttachment) {
                const formData = new FormData();
                formData.append('contract', this.contract._id);
                formData.append('documentForm', this.documentForm._id);
                formData.append('questionId', question?._id ?? '');
                formData.append('answerId', answer?._id ?? '');
                formData.append('removedAttachment', attachment._id)
                this.documentFormService.updateAttachmentTypeQuestion(formData).subscribe((res) => {
                        if (res.success) {
                                this.toastService.success('Answer updated successfully')
                                this.updateAnswersFromSocket([res.data])
                        }
                })
        }

        hideUploadFileDialog() {
                this.uploadFileDialog = false;
        }

        onFileRemoveFromDialog(file: File) {
                this.uploadDialogFiles = this.uploadDialogFiles.filter(f => f !== file);
        }

        viewAttachment(answer: IAttachment) {
                this.selectedDocumentFile = answer;
                this.selectedDocumentId = answer._id;
                this.showDocumentPreview = true;
        }

        onQuestionZoomMode(form: IDocumentFormQA) {
                this.selectedFormForZoom = form;
                this.loadAllQuestions();
                this.setCurrentQuestionIndex(form);
                this.updateSharedSources();
                this.zoomModeActiveTabIndex.set('0');
                this.showZoomModeDialog = true;
        }

        loadAllQuestions() {
                this.allQuestions = [];
                if (this.documentForm?.sections) {
                        this.documentForm.sections.forEach(section => {
                                this.allQuestions.push(...section.form);
                        });
                }
                this.totalQuestions = this.allQuestions.length;
        }

        setCurrentQuestionIndex(targetForm: IDocumentFormQA) {
                const index = this.allQuestions.findIndex(form =>
                        form.question._id === targetForm.question._id
                );
                this.currentQuestionIndex = index !== -1 ? index + 1 : 1;
        }

        onNextQuestion() {
                this.zoomModeActiveTabIndex.set('0');
                if (this.currentQuestionIndex < this.totalQuestions) {
                        this.currentQuestionIndex++;
                        this.selectedFormForZoom = this.allQuestions[this.currentQuestionIndex - 1];
                        this.updateSharedSources();
                }
        }

        onPreviousQuestion() {
                this.zoomModeActiveTabIndex.set('0');
                if (this.currentQuestionIndex > 1) {
                        this.currentQuestionIndex--;
                        this.selectedFormForZoom = this.allQuestions[this.currentQuestionIndex - 1];
                        this.updateSharedSources();
                }
        }



        // Action methods for zoom mode
        flagQuestionInZoom() {
                if (this.selectedFormForZoom) {
                        this.flagQuestion(this.selectedFormForZoom.question);
                        this.refreshSelectedFormForZoom();
                }
        }

        retryQuestionInZoom(event: Event) {
                if (this.selectedFormForZoom) {
                        this.retryQuestion(event, this.selectedFormForZoom.answer._id);
                        this.refreshSelectedFormForZoom();
                }
        }

        openWatcherOverlayInZoom(event: Event) {
                if (this.selectedFormForZoom) {
                        this.refreshSelectedFormForZoom();
                }
        }

        addQuestionToQnAInZoom() {
                if (this.selectedFormForZoom) {
                        this.addQuestionToQnA(this.selectedFormForZoom.question, this.selectedFormForZoom.answer);
                        this.refreshSelectedFormForZoom();
                }
        }

        approveQuestionInZoom() {
                if (this.selectedFormForZoom) {
                        this.approveQuestion(this.selectedFormForZoom.question);
                        this.refreshSelectedFormForZoom();
                }
        }

        rejectQuestionInZoom() {
                if (this.selectedFormForZoom) {
                        this.rejectQuestion(this.selectedFormForZoom.question);
                        this.refreshSelectedFormForZoom();
                }
        }

        assignUserToQuestionInZoom(event: Event) {
                if (this.selectedFormForZoom) {
                        this.assignUserToQuestion(this.selectedFormForZoom.question, event);
                        this.refreshSelectedFormForZoom();
                }
        }

        refreshSelectedFormForZoom() {
                if (this.selectedFormForZoom && this.documentForm?.sections) {
                        const currentQuestionId = this.selectedFormForZoom.question._id;
                        this.loadAllQuestions();
                        const updatedForm = this.allQuestions.find(form =>
                                form.question._id === currentQuestionId
                        );

                        if (updatedForm) {
                                this.selectedFormForZoom = updatedForm;
                                const newIndex = this.allQuestions.findIndex(form =>
                                        form.question._id === currentQuestionId
                                );
                                if (newIndex !== -1) {
                                        this.currentQuestionIndex = newIndex + 1;
                                }
                                this.updateSharedSources();
                        }
                }
        }

        updateSharedSources() {
                if (this.selectedFormForZoom) {
                        this.questionSourceService.updateSources({
                                questionId: this.selectedFormForZoom.question._id,
                                sources: this.selectedFormForZoom.answer.sources || []
                        });
                }
        }

        getCurrentSources(): { questionId: string, sources: IFormAnswer['sources'] } {
                if (this.selectedFormForZoom) {
                        return {
                                questionId: this.selectedFormForZoom.question._id,
                                sources: this.selectedFormForZoom.answer.sources || []
                        };
                }
                return this.sharedSourcesService.getCurrentSources();
        }

        openSubMenu(event: MouseEvent, triggerEl: HTMLElement, filter: 'Status' | 'Author' | 'Assignee') {
                event.stopPropagation();
                this.statusFilterOverlay.hide();
                this.authorFilterOverlay.hide()
                this.assigneeFilterOverlay.hide()
                let currentOverlay!: PopoverCompatibility;
                switch (filter) {
                        case 'Status': {
                                currentOverlay = this.statusFilterOverlay.popover
                                break;
                        }
                        case 'Author': {
                                currentOverlay = this.authorFilterOverlay.popover
                                break;
                        }
                        case 'Assignee': {
                                currentOverlay = this.assigneeFilterOverlay.popover
                                break;
                        }
                }
                currentOverlay.show(event, triggerEl);
                setTimeout(() => {
                        const rect = triggerEl.getBoundingClientRect();
                        const panel = currentOverlay.container as HTMLElement;
                        if (panel) {
                                panel.style.position = 'fixed';
                                panel.style.left = (rect.left - panel.offsetWidth) + 'px'; // left side
                                panel.style.top = rect.top + 'px'; // align vertically
                        }
                });
        }

        checkIfAllStatusesSelected() {
                return ((this.selectedFilter.questionStatuses?.length ?? 0) + (this.selectedFilter.answerStatuses?.length ?? 0) == 5)
        }

        selectAllStatus(event: CheckboxChangeEvent) {
                this.selectedFilter.questionStatuses = event.checked ? [FORM_QUESTION_STATUS.FLAGGED, FORM_QUESTION_STATUS.APPROVED, FORM_QUESTION_STATUS.REJECTED] : []
                this.selectedFilter.answerStatuses = event.checked ? [ANSWER_GENERATION_STATUS.EXTRACTED, ANSWER_GENERATION_STATUS.NOT_EXTRACTED] : []
        }

        statusCheckboxChanged(value: FORM_QUESTION_STATUS | ANSWER_GENERATION_STATUS, checked: boolean, type: 'Question' | 'Answer', applyFilters: boolean = false) {
                if (checked) {
                        if (type == 'Question') {
                                this.selectedFilter.questionStatuses?.push(value as FORM_QUESTION_STATUS)
                        }
                        else {
                                this.selectedFilter.answerStatuses?.push(value as ANSWER_GENERATION_STATUS)
                        }
                }
                else {
                        if (type == 'Question') {
                                this.selectedFilter.questionStatuses = this.selectedFilter.questionStatuses?.filter(status => status != value)
                        }
                        else {
                                this.selectedFilter.answerStatuses = this.selectedFilter.answerStatuses?.filter(status => status != value)
                        }
                }
                if (applyFilters) {
                        this.fetchDocumentForm(this.selectedFilter)
                }
        }

        authorCheckboxChanged(value: ANSWER_GENERATION_TYPE, checked: boolean, applyFilters: boolean = false) {
                if (checked) {
                        this.selectedFilter.authors?.push(value)
                }
                else {
                        this.selectedFilter.authors = this.selectedFilter.authors?.filter(author => author != value)
                }
                if (applyFilters) {
                        this.fetchDocumentForm(this.selectedFilter)
                }
        }

        filterAssigneesInFilter() {
                if (!this.filterAssigneeSearchValue.trim()) {
                        let allUsers = [...this.users];
                        const projectManagerIds = this.getProjectManagerIds();
                        if (projectManagerIds.length > 0) {
                                const managers = allUsers.filter(user => projectManagerIds.includes(user._id));
                                const nonManagers = allUsers.filter(user => !projectManagerIds.includes(user._id));
                                this.filteredAssignees = [...managers, ...nonManagers];
                        } else {
                                this.filteredAssignees = allUsers;
                        }
                        return;
                }

                const query = this.filterAssigneeSearchValue.toLowerCase().trim();
                let filteredUsers = this.users.filter((user: IUser) => {
                        const fullName = (user.fullName || '').toLowerCase();
                        const firstName = (user.firstName || '').toLowerCase();
                        const lastName = (user.lastName || '').toLowerCase();
                        return fullName.includes(query) || firstName.includes(query) || lastName.includes(query);
                });

                const projectManagerIds = this.getProjectManagerIds();
                if (projectManagerIds.length > 0) {
                        const matchingManagers = filteredUsers.filter(user => projectManagerIds.includes(user._id));
                        const nonManagers = filteredUsers.filter(user => !projectManagerIds.includes(user._id));
                        this.filteredAssignees = [...matchingManagers, ...nonManagers];
                } else {
                        this.filteredAssignees = filteredUsers;
                }
        }

        assigneeCheckboxChanged(value: string, checked: boolean, applyFilter: boolean = false) {
                if (checked) {
                        this.selectedFilter.assignees?.push(value)
                }
                else {
                        this.selectedFilter.assignees = this.selectedFilter.assignees?.filter(user => user != value)
                }
                if (applyFilter) {
                        this.fetchDocumentForm(this.selectedFilter)
                }
        }

        applyFilters() {
                this.filterMainOverlay.hide()
                this.statusFilterOverlay.hide()
                this.authorFilterOverlay.hide()
                this.assigneeFilterOverlay.hide()
                this.fetchDocumentForm(this.selectedFilter)
        }

        onCloseEditForm() {
                this.showEditForm = false;
                this.fetchDocumentForm(this.selectedFilter);
        }

        getProjectManagerIds(): string[] {
                const contractWithProject = this.contract as any;
                if (contractWithProject?.project && typeof contractWithProject.project === 'object') {
                        const project = contractWithProject.project;
                        if (project.managers && Array.isArray(project.managers) && project.managers.length > 0) {
                                return project.managers;
                        }
                }
                return [];
        }

        isProjectManager(userId: string): boolean {
                const projectManagerIds = this.getProjectManagerIds();
                return projectManagerIds.includes(userId);
        }

}
